import {
  CancelReason,
  DbSchema,
  ErrorSchema,
  Nullable,
  OrderBySchema,
  OrderChangelog,
  OrderDivisionFilterSchema,
  PaginationInfoSchema,
  PaginationSchema,
  paramByIDSchema,
  paramOrderItemSchema,
  ProductWithPromoResponseSchema,
  readActivitiesSchema,
  ReadActivityDetailSchema,
  ReadManyOrderHistorySchema,
  ReadManyOrderTemplateItemSchema,
  ReadManyOrderTemplateSchema,
  SortFilterSchema,
  StorageFilterSchema,
  TypeCustom,
  VOUCHER_CODE_MAX_LENGTH,
  WarehouseIdSchema,
} from '@diamond/shared/schema';
import { RecursiveStatic } from '@diamond/shared/schema';
import {
  ProductPaymentMethodResponseFieldsSchema,
  ProductResponseSchema,
} from '@diamond/shared/schema/product';
import { NoteValidationSchema } from '@diamond/shared/validation';
import { SavedMultipartFile } from '@fastify/multipart';
import { TObject, Type } from '@sinclair/typebox';

import { FinalizedPaymentProofItemSchema } from './payment-proof-ocr.schema';

export const VoucherRuleProductSchema = Type.Object({
  is_rule_product: Type.Boolean(),
  voucher_codes: Type.Array(Type.String()),
  rules: Type.Array(
    Type.Object({
      voucher_code: Type.String(),
      voucher_name: Type.String(),
      min_quantity: Type.Integer(),
      reward_type: Type.String(),
    })
  ),
});

export const PopulatedOrderPromoSchema = Type.Omit(DbSchema['order_promos'], [
  'id',
  'order_id',
  'created_at',
  'updated_at',
]);
export const OrderUserSchema = Type.Omit(DbSchema['users'], [
  'created_at',
  'updated_at',
  'deleted_at',
  'created_by',
  'updated_by',
  'deleted_by',
]);

// MBG Manual Process Item Schema
export const ManualProcessItemSchema = Type.Object({
  product_item_code: Type.Union([Type.String(), Type.Number()]),
  ordered_quantity: Type.Number(),
  threshold_quantity: Type.Number(),
  distribution_channel: Type.Optional(Type.String()),
  shipto_id: Type.Optional(Type.String({ format: 'uuid' })),
  config_source: Type.Optional(
    Type.Union([
      Type.Literal('specific_shipto'),
      Type.Literal('general_channel'),
    ])
  ),
});

export const OrderPaymentSchema = Type.Union([
  Type.Intersect([
    Type.Omit(DbSchema['order_payments'], ['order_meta_id']),
    Type.Object({
      proofs: Type.Array(
        Type.Pick(DbSchema['order_payment_proofs'], ['id', 'url', 'created_at'])
      ),
      total_diff: Type.Number(),
    }),
  ]),
  Type.Null(),
]);

const InstructionPaymentSubStateSchema = Type.Literal('instruction');
const ProofDraftPaymentSubStateSchema = Type.Literal('proof_draft');

export const PaymentSubStateSchema = Type.Union([
  InstructionPaymentSubStateSchema,
  ProofDraftPaymentSubStateSchema,
  Type.Literal('done'),
]);

const ActionablePaymentSubStateSchema = Type.Union([
  InstructionPaymentSubStateSchema,
  ProofDraftPaymentSubStateSchema,
]);

const AdminOrderPaymentOcrSourceSchema = Type.Union([
  Type.Literal('ocr'),
  Type.Literal('manual'),
  Type.Null(),
]);

const AdminOrderPaymentOcrWarningCodeSchema = Type.Union([
  Type.Literal('LOW_CONFIDENCE'),
  Type.Literal('MANUAL_SENDER_BANK'),
  Type.Literal('MANUAL_AMOUNT'),
  Type.Literal('MANUAL_RECIPIENT_NAME'),
  Type.Literal('MANUAL_TRANSACTION_DATETIME'),
  Type.Literal('OCR_UNREADABLE_MANUAL_FALLBACK'),
  Type.Literal('OCR_UNAVAILABLE_MANUAL_FALLBACK'),
  Type.Literal('OCR_TIMEOUT_MANUAL_FALLBACK'),
  Type.Literal('OCR_JOB_NOT_READY_AT_SUBMIT'),
  Type.Literal('PAYMENT_PROOF_AMOUNT_MISMATCH'),
  Type.Literal('AMOUNT_VALIDATION_INCONCLUSIVE'),
  Type.Literal('CONFIDENCE_VALIDATION_INCONCLUSIVE'),
  Type.Literal('RECIPIENT_MISMATCH'),
  Type.Literal('RECIPIENT_VALIDATION_INCONCLUSIVE'),
  Type.Literal('LEGACY_PROOF_NO_OCR'),
  Type.Literal('POSSIBLE_DUPLICATE_REFERENCE'),
]);

const AdminOrderPaymentProofDisplaySchema = Type.Object({
  state: Type.Union([Type.Literal('success'), Type.Literal('warning')]),
  warning_labels: Type.Array(Type.String()),
});

export const AdminOrderPaymentOcrSchema = Type.Object({
  confidence_score: Nullable(Type.Number()),
  confidence_threshold: Type.Number(),
  is_confidence_passed: Type.Boolean(),
  amount: Nullable(Type.Integer()),
  amount_source: AdminOrderPaymentOcrSourceSchema,
  recipient_name: Nullable(Type.String()),
  recipient_name_source: AdminOrderPaymentOcrSourceSchema,
  recipient_account_number: Nullable(Type.String()),
  transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
  transaction_datetime_source: AdminOrderPaymentOcrSourceSchema,
  sender_account_name: Nullable(Type.String()),
  sender_bank: Nullable(Type.String()),
  sender_bank_source: AdminOrderPaymentOcrSourceSchema,
  manual_input_reason: Nullable(Type.String()),
  original_ocr: Type.Object({
    amount: Nullable(Type.Integer()),
    recipient_name: Nullable(Type.String()),
    transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
    sender_bank: Nullable(Type.String()),
  }),
  manual_override: Type.Object({
    amount: Nullable(Type.Integer()),
    recipient_name: Nullable(Type.String()),
    transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
    sender_bank: Nullable(Type.String()),
    manual_input_reason: Nullable(Type.String()),
    manual_input_at: Nullable(Type.String({ format: 'date-time' })),
  }),
  checks: Type.Object({
    amount: Type.Union([
      Type.Literal('matched'),
      Type.Literal('attention'),
      Type.Literal('inconclusive'),
    ]),
    recipient: Type.Union([
      Type.Literal('passed'),
      Type.Literal('attention'),
      Type.Literal('inconclusive'),
    ]),
    possible_duplicate_reference: Type.Union([
      Type.Literal('passed'),
      Type.Literal('attention'),
    ]),
    confidence: Type.Union([
      Type.Literal('passed'),
      Type.Literal('attention'),
      Type.Literal('inconclusive'),
    ]),
    manual_input: Type.Union([
      Type.Literal('passed'),
      Type.Literal('attention'),
    ]),
    manual_amount: Type.Union([
      Type.Literal('passed'),
      Type.Literal('attention'),
    ]),
    manual_recipient: Type.Union([
      Type.Literal('passed'),
      Type.Literal('attention'),
    ]),
    manual_transaction_datetime: Type.Union([
      Type.Literal('passed'),
      Type.Literal('attention'),
    ]),
  }),
  warnings: Type.Array(
    Type.Object({
      code: AdminOrderPaymentOcrWarningCodeSchema,
      message: Type.String(),
    })
  ),
});

export const AdminOrderPaymentProofSchema = Type.Intersect([
  Type.Pick(DbSchema['order_payment_proofs'], ['id', 'url', 'created_at']),
  Type.Object({
    uploaded_at: Nullable(Type.String({ format: 'date-time' })),
    ocr: Type.Union([AdminOrderPaymentOcrSchema, Type.Null()]),
    admin_display: Type.Union([
      AdminOrderPaymentProofDisplaySchema,
      Type.Null(),
    ]),
    warning_codes: Type.Array(AdminOrderPaymentOcrWarningCodeSchema),
  }),
]);

export const AdminOrderPaymentSchema = Type.Union([
  Type.Intersect([
    Type.Omit(DbSchema['order_payments'], ['order_meta_id']),
    Type.Object({
      proofs: Type.Array(AdminOrderPaymentProofSchema),
      total_diff: Type.Number(),
    }),
  ]),
  Type.Null(),
]);

export const OrderVoucherRewardDiscountSchema = Type.Intersect([
  Type.Pick(DbSchema['order_voucher_reward_discounts'], [
    'product_item_code',
    'product_name',
    'division',
    'quantity',
    'original_subtotal',
    'discount_amount',
    'discount_percent',
    'saved_amount',
    'final_subtotal',
  ]),
  Type.Object({
    reward_type: Type.Union([
      Type.Literal('DISCOUNT_PERCENT'),
      Type.Literal('DISCOUNT_AMOUNT'),
      Type.Literal('FREE_GOODS'),
    ]),
  }),
]);

export const OrderVoucherRewardFreeGoodSchema = Type.Intersect([
  Type.Pick(DbSchema['order_voucher_reward_free_goods'], [
    'product_item_code',
    'product_name',
    'division',
    'quantity',
    'saved_amount',
  ]),
  Type.Object({
    product_images: Type.Array(
      Type.Object({
        image: Type.String(),
        thumbnail_url: Nullable(Type.String()),
        medium_image_url: Nullable(Type.String()),
        large_image_url: Nullable(Type.String()),
      }),
      { default: [] }
    ),
    reward_type: Type.Union([
      Type.Literal('DISCOUNT_PERCENT'),
      Type.Literal('DISCOUNT_AMOUNT'),
      Type.Literal('FREE_GOODS'),
    ]),
  }),
]);

export const OrderVoucherAppliedSchema = Type.Object({
  order_voucher_id: Type.String({ format: 'uuid' }),
  voucher_id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  voucher_code: Type.String({ maxLength: VOUCHER_CODE_MAX_LENGTH }),
  voucher_title: Type.String(),
  voucher_target_type: Type.Union([
    Type.Literal('GENERAL'),
    Type.Literal('SHIP_TO'),
  ]),
  period_start: Type.String(),
  period_end: Type.String(),
  reward_type: Type.Union([
    Type.Literal('DISCOUNT_PERCENT'),
    Type.Literal('DISCOUNT_AMOUNT'),
    Type.Literal('FREE_GOODS'),
  ]),
  total_discount_amount: Type.Number({ minimum: 0 }),
  min_purchase_amount: Nullable(Type.Number({ minimum: 0 })),
  min_total_quantity_all_item: Nullable(Type.Integer({ minimum: 0 })),
  is_bundling: Type.Boolean(),
  allow_other_vouchers: Type.Boolean(),
  allow_other_promos: Type.Boolean(),
  allow_special_prices: Type.Boolean(),
  product_discounts: Type.Array(OrderVoucherRewardDiscountSchema),
  free_goods: Type.Array(OrderVoucherRewardFreeGoodSchema),
});

export const OrderVoucherSummarySchema = Type.Object({
  applied_vouchers: Type.Array(OrderVoucherAppliedSchema),
  total_voucher_discount: Type.Number({ minimum: 0 }),
  reward_by_item_code: Type.Record(
    Type.String(),
    Type.Object({
      discounts: Type.Array(OrderVoucherRewardDiscountSchema),
      free_goods: Type.Array(OrderVoucherRewardFreeGoodSchema),
    })
  ),
});

export const CreateOrderInsufficientStockItemSchema = Type.Object({
  item_code: Type.Integer(),
  title: Type.String(),
  requested: Type.Integer({ minimum: 0 }),
  available: Type.Integer({ minimum: 0 }),
  sales_unit_desc: Nullable(Type.String()),
});

export const CreateOrderInsufficientStockErrorResponseSchema = Type.Object({
  error: Type.String(),
  message: Type.String(),
  statusCode: Type.Literal(422),
  data: Type.Object({
    code: Type.Literal('INSUFFICIENT_STOCK'),
    items: Type.Array(CreateOrderInsufficientStockItemSchema),
  }),
});

export const CreateOrderUnprocessableEntityResponseSchema = Type.Union([
  CreateOrderInsufficientStockErrorResponseSchema,
  ErrorSchema,
]);

const OrderFulfillmentSnapshotFields = [
  'checkout_original_subtotal',
  'checkout_subtotal',
  'checkout_saved_subtotal',
  'processed_original_subtotal',
  'processed_subtotal',
  'processed_saved_subtotal',
  'delivered_original_subtotal',
  'delivered_subtotal',
  'delivered_saved_subtotal',
  'received_original_subtotal',
  'received_subtotal',
  'received_saved_subtotal',
  'latest_original_subtotal',
  'latest_subtotal',
  'latest_saved_subtotal',
  'checkout_total_items',
  'processed_total_items',
  'delivered_total_items',
  'received_total_items',
] as const;

const OrderItemFulfillmentSnapshotFields = [
  'checkout_quantity',
  'checkout_original_subtotal',
  'checkout_subtotal',
  'checkout_saved_subtotal',
  'checkout_original_unit_price',
  'checkout_unit_price',
  'processed_quantity',
  'processed_original_subtotal',
  'processed_subtotal',
  'processed_saved_subtotal',
  'processed_original_unit_price',
  'processed_unit_price',
  'delivered_quantity',
  'delivered_subtotal',
  'delivered_original_subtotal',
  'delivered_saved_subtotal',
  'delivered_original_unit_price',
  'delivered_unit_price',
  'received_quantity',
  'received_subtotal',
  'received_original_subtotal',
  'received_saved_subtotal',
  'received_original_unit_price',
  'received_unit_price',
  'latest_quantity',
  'latest_original_subtotal',
  'latest_subtotal',
  'latest_saved_subtotal',
  'latest_original_unit_price',
  'latest_unit_price',
] as const;

const OrderItemOmitBaseFields = [
  'id',
  'order_id',
  'product_id',
  'line_item',
  'line_item_sap',
  'original_price',
  'original_stock',
] as const;

const OrderItemFulfillmentQtySubtotalOmitFields = [
  'processed_quantity',
  'delivered_quantity',
  'received_quantity',
  'processed_subtotal',
  'delivered_subtotal',
  'received_subtotal',
] as const;

const buildOrderItemSchema = <TProductResponseExtension extends TObject>(
  orderItemOmitFields: readonly (
    | (typeof OrderItemOmitBaseFields)[number]
    | (typeof OrderItemFulfillmentQtySubtotalOmitFields)[number]
  )[],
  productResponseExtension: TProductResponseExtension
) =>
  Type.Array(
    Type.Intersect([
      Type.Omit(DbSchema['orders'], [
        'order_meta_id',
        'so_number',
        'is_submitted',
        'po_effective_date',
      ]),
      Type.Object({
        reason: Type.Optional(Nullable(Type.String())),
        cancel_reason: Type.Optional(Nullable(Type.String())),
        cancel_reason_id: Type.Optional(Nullable(Type.String())),
        products: Type.Array(
          Type.Intersect([
            Type.Omit(ProductResponseSchema, [
              'wishlist_id',
              'sub_category_name',
              'sub_third_category_name',
              'prices',
              'stock_status',
            ]),
            Type.Omit(DbSchema['order_items'], orderItemOmitFields),
            Type.Object({
              stock_magnifier: Type.Integer(),
              subtotal_diff: Type.Number(),
              voucher_discounts: Type.Array(OrderVoucherRewardDiscountSchema),
              voucher_free_goods: Type.Array(OrderVoucherRewardFreeGoodSchema),
            }),
            productResponseExtension,
          ])
        ),
        promos: PopulatedOrderPromoSchema,
      }),
    ])
  );

export const OrderItemSchema = buildOrderItemSchema(
  [
    ...OrderItemOmitBaseFields,
    ...OrderItemFulfillmentQtySubtotalOmitFields,
  ],
  Type.Object({})
);

export const StockConfirmationStatusSchema = Type.Union([
  Type.Literal('waiting_confirmation'),
  Type.Literal('confirmed'),
  Type.Null(),
]);

export const GetOneOrderItemSchema = buildOrderItemSchema(
  [...OrderItemOmitBaseFields],
  Type.Intersect([
    ProductPaymentMethodResponseFieldsSchema,
    Type.Object({
      stock_confirmation_status: StockConfirmationStatusSchema,
    }),
  ])
);

export const OrderCrudSchema = {
  path: '/order',

  // ------------------------------------------------------
  // Order ( / )
  // ------------------------------------------------------
  createOrder: {
    path: '',
    body: Type.Object({
      warehouse_id: Type.String({ format: 'uuid' }),
      products: Type.Array(
        Type.Object({
          id: Type.String({ format: 'uuid' }),
          quantity: Type.Integer({ exclusiveMinimum: 0 }),
          note: Type.Optional(NoteValidationSchema),
        }),
        { minItems: 1 }
      ),
      voucher_codes: Type.Optional(
        Type.Array(
          Type.String({
            minLength: 1,
            maxLength: VOUCHER_CODE_MAX_LENGTH,
          })
        )
      ),
    }),
    response: Type.Omit(DbSchema['order_metas'], ['is_dummy']),
    unprocessable_entity_response: CreateOrderUnprocessableEntityResponseSchema,
  },
  createSubmittedOrder: {
    path: '/meta/:id/submit',
    params: paramByIDSchema,
    body: Type.Intersect([
      WarehouseIdSchema,
      Type.Object({
        excludeVoucher: Type.Optional(Type.Boolean()),
      }),
    ]),
    response: Type.Intersect([
      Type.Omit(DbSchema['order_metas'], ['purchase_order_docs', 'is_dummy']),
      Type.Object({
        payment: OrderPaymentSchema,
        items: OrderItemSchema,
        vouchers: OrderVoucherSummarySchema,
        total_voucher_discount: Type.Number({ minimum: 0 }),
        purchase_order_docs: Type.Array(
          Type.Pick(DbSchema['order_po_docs'], ['order_meta_id', 'url'])
        ),
      }),
    ]),
    forbidden_response: Type.Object({
      message: Type.String(),
      error: Type.String(),
      statusCode: Type.Number(),
      data: Type.Optional(
        Type.Array(
          Type.Object({
            product_image: Type.String(),
            product_title: Type.String(),
            item_code: Type.Integer(),
            quantity: Type.Integer({ exclusiveMinimum: 0 }),
            price: Type.Number(),
            order_item_id: Type.String({ format: 'uuid' }),
            order_id: Type.String({ format: 'uuid' }),
            order_original_subtotal: Type.Number(),
            order_subtotal: Type.Number(),
            order_item_original_subtotal: Type.Number(),
            order_item_subtotal: Type.Number(),
            order_item_type: Nullable(Type.String()),
            order_division: Type.Union([
              Type.Literal('diamond'),
              Type.Literal('trading'),
              Type.Literal('fnb'),
              Type.Literal('equipment'),
              Type.Literal('food'),
            ]),
          })
        )
      ),
    }),
  },
  getOneOrder: {
    path: '/meta/:id',
    query: WarehouseIdSchema,
    params: paramByIDSchema,
    response: Type.Intersect([
      Type.Omit(DbSchema['order_metas'], ['purchase_order_docs', 'is_dummy']),
      Type.Object({
        po_effective_date: Type.Number({ minimum: 0 }),
        stock_magnifier: Type.Integer(),
        transaction_date: Type.Number({ minimum: 0 }),
        payment: OrderPaymentSchema,
        items: GetOneOrderItemSchema,
        vouchers: OrderVoucherSummarySchema,
        total_voucher_discount: Type.Number({ minimum: 0 }),
        bank_account: Type.Union([
          Type.Intersect([
            Type.Omit(DbSchema['bank_accounts'], ['id']),
            Type.Object({
              bank_account_holder: Type.String(),
            }),
          ]),
          Type.Null(),
        ]),
        purchase_order_docs: Type.Array(
          Type.Pick(DbSchema['order_po_docs'], ['order_meta_id', 'url', 'id'])
        ),
        original_total: Type.Integer(),
        rejected_items_count: Type.Integer({ minimum: 0 }),
        rejected_items_string: Type.Union([Type.String(), Type.Null()]),
        updated_items_count: Type.Integer({ minimum: 0 }),
        updated_items_string: Type.Union([Type.String(), Type.Null()]),
        payment_sub_state: Type.Union([PaymentSubStateSchema, Type.Null()]),
      }),
    ]),
  },
  updateOrder: {
    path: '/meta/:id',
    params: paramByIDSchema,
    body: Type.Intersect([
      Type.Pick(DbSchema['order_metas'], ['payment_method']),
      Type.Object({
        delivery_note: Type.Optional(NoteValidationSchema),
      }),
      Type.Object({
        purchase_order_docs: Type.Optional(
          Type.Array(Type.String({ format: 'uri', minLength: 1 }))
        ),
      }),
      Type.Object({
        ship_to_contact: Type.Object({
          name: Type.String({ minLength: 1 }),
          telp: Type.String({ minLength: 1 }),
        }),
      }),
      Type.Partial(
        Type.Pick(DbSchema['order_metas'], ['delivery_option', 'delivery_date'])
      ),
    ]),
    response: Type.Intersect([
      Type.Omit(DbSchema['order_metas'], [
        'purchase_order_docs',
        'is_dummy',
        'original_total', // temporary omitting, not touching backoffice yet
        'saved_total', // temporary omitting, not touching backoffice yet
      ]),
      Type.Object({
        purchase_order_docs: Type.Array(
          Type.Pick(DbSchema['order_po_docs'], ['order_meta_id', 'url', 'id'])
        ),
      }),
    ]),
  },
  uploadPurchaseOrderDocument: {
    path: '/purchase-order-document/upload',
    body: Type.Object({
      files: TypeCustom.Custom<SavedMultipartFile[]>(),
    }),
    response: Type.Object({
      urls: Type.Array(Type.String()),
    }),
  },
  deletePurchaseOrderDocument: {
    path: '/purchase-order-document/:id',
    params: paramByIDSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },
  updatePaymentProof: {
    path: '/payment-proof/:id',
    params: paramByIDSchema,
    body: Type.Intersect([
      Type.Pick(DbSchema['order_payments'], ['bank_name', 'bank_account']),
      Type.Object({
        url: Type.Array(Type.String({ format: 'uri' })),
      }),
    ]),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  waitingPaymentsSummary: {
    path: '/waiting-payments/summary',
    query: WarehouseIdSchema,
    response: Type.Object({
      count: Type.Integer({ minimum: 0 }),
      single_order: Nullable(
        Type.Object({
          order_meta_id: Type.String({ format: 'uuid' }),
          payment_sub_state: ActionablePaymentSubStateSchema,
        })
      ),
    }),
  },
  historyOrders: {
    path: '/histories',
    query: ReadManyOrderHistorySchema,
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['orders'], [
            'id',
            'order_meta_id',
            'original_subtotal',
            'subtotal',
            'division',
            'sub_po_number',
            'so_number',
            'delivery_link',
            'invoice_link',
            'status',
            'sap_note',
            'po_effective_date',
            ...OrderFulfillmentSnapshotFields,
          ]),
          Type.Object({
            warehouse_id: Type.String({ format: 'uuid' }),
            transaction_date: Type.Number({ minimum: 0 }),
            expired_at: Type.Optional(Type.Number({ minimum: 0 })),
            transaction_id: Type.String(),
            payment_sub_state: Type.Union([PaymentSubStateSchema, Type.Null()]),
            sap_no_shipto: Type.Optional(Type.Integer()),
            items: Type.Array(
              Type.Intersect([
                Type.Pick(ProductResponseSchema, [
                  'title',
                  'division',
                  'desc',
                  'brand_name',
                  'brand_desc',
                  'item_code',
                ]),
                Type.Object({
                  images: Type.Array(Type.String()),
                  image_thumbnails: Type.Array(Type.String()),
                  images_medium: Type.Array(Type.String()),
                  images_large: Type.Array(Type.String()),
                }),
                Type.Pick(DbSchema['order_items'], [
                  'quantity',
                  ...OrderItemFulfillmentSnapshotFields,
                ]),
              ])
            ),
            stock_magnifier: Type.Integer(),
            rejected_items_count: Type.Integer({ minimum: 0 }),
            rejected_items_string: Type.Union([Type.String(), Type.Null()]),
            updated_items_count: Type.Integer({ minimum: 0 }),
            updated_items_string: Type.Union([Type.String(), Type.Null()]),
            reason: Nullable(Type.String()),
            cancel_reason: Nullable(Type.String()),
            cancel_reason_id: Nullable(Type.String()),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  updateOrderWebhook: {
    path: '/hook/sap',
    body: Type.Object({
      DETAIL: Type.Array(
        Type.Object({
          QTY_PGI: Type.Number(),
          QTY_POD: Type.Number(),
          MATERIAL: Type.Number(),
          SO_LINES: Type.Number(),
          SALES_UNIT: Type.String(),
          DELIVERY_QTY: Type.Number(),
          DELIVERY_LINES: Type.Number(),
          DELIVERY_NUMBER: Type.Number(),
          CANCEL_REASON: Type.String(),
        })
      ),
      ADDRESS: Type.String(),
      PGI_DATE: Type.String(),
      PGI_TIME: Type.String(),
      POD_DATE: Type.String(),
      POD_TIME: Type.String(),
      SAPSTATUS: Type.Union([
        Type.Literal('Not yet processed'),
        Type.Literal('Completely processed'),
        Type.Literal('Partially processed'),
        Type.Literal('Not Relevant'),
      ]),
      SO_NUMBER: Type.Number(),
      CREATED_ON: Type.String(),
      POD_STATUS: Type.Union([
        Type.Literal('A'),
        Type.Literal('B'),
        Type.Literal('C'),
      ]),
      SHIPMENT_NO: Type.Number(),
      CREATED_TIME: Type.String(),
      PICKING_DATE: Type.String(),
      PICKING_TIME: Type.String(),
      SAPDELSTATUS: Type.Union([
        Type.Literal('Partially Delivered'),
        Type.Literal('Fully Delivered'),
        Type.Literal('Not Delivered'),
        Type.Literal('Not Relevant'),
      ]),
      SAPREJSTATUS: Type.Union([
        Type.Literal('Partial Rejected'),
        Type.Literal('Partially Rejected'),
        Type.Literal('Everything Rejected'),
        Type.Literal('Nothing Rejected'),
      ]),
      CUSTOMER_NAME: Type.String(),
      DELIVERY_DATE: Type.String(),
      SHIPMENT_DATE: Type.String(),
      SHIPMENT_TIME: Type.String(),
      SHIP_TO_PARTY: Type.String(),
      BILLING_STATUS: Type.String(),
      PICKING_STATUS: Type.String(),
      REQ_DELIV_DATE: Type.String(),
      DELIVERY_NUMBER: Type.Number(),
      SHIPMENT_STATUS: Type.Union([
        Type.Literal('A'),
        Type.Literal('B'),
        Type.Literal('C'),
      ]),
      GOOD_MOVEMENT_STATUS: Type.Union([
        Type.Literal('A'),
        Type.Literal('B'),
        Type.Literal('C'),
      ]),
      INVOICELINK: Type.Optional(Type.String()),
      DELIVERYLINK: Type.Optional(Type.String()),
      CANCEL_REASON: Type.Optional(Type.String()),
    }),
    response: Type.Object({ message: Type.String() }),
  },
  getOrderFilter: {
    path: '/filters',
    query: Type.Object({
      distribution_channel: Type.String(),
    }),
    response: Type.Object({
      divisions: OrderDivisionFilterSchema,
      storages: StorageFilterSchema,
      sorts: SortFilterSchema,
    }),
  },
  shipmentDates: {
    path: '/shipment-dates',
    query: WarehouseIdSchema,
    response: Type.Object({
      dates_available: Type.Array(Type.String()),
      dates_available_ui: Type.Array(Type.String()),
    }),
  },
  getOrderValidation: {
    path: '/meta/:id/order-validation',
    params: paramByIDSchema,
    response: Type.Object({
      code: Type.Union([
        Type.Literal('DIFF_PRICE_OR_STOCK'),
        Type.Literal('ALL_ORDER_READY_TO_PROCESS'),
        Type.Literal('PARTIALLY_OUT_OF_STOCK'),
        Type.Literal('INVALID_PROMOS_PRESENT'),
      ]),
      message: Type.String(),
      invalid_promos: Type.Pick(DbSchema['order_promos'], [
        'single_free_good_promos',
        'single_discount_promos',
        'bundled_free_good_promos',
        'bundled_discount_promos',
      ]),
      voucher_validation: Type.Optional(
        Type.Object({
          invalid_vouchers: Type.Array(
            Type.Object({
              voucher_id: Type.String(),
              voucher_code: Type.String(),
              reason: Type.Union([
                Type.Literal('period_inactive'),
                Type.Literal('quota_exhausted'),
                Type.Literal('per_shipto_limit_exceeded'),
                Type.Literal('min_purchase_not_met'),
                Type.Literal('bundling_quantity_not_met'),
                Type.Literal('rule_products_not_met'),
                Type.Literal('free_goods_insufficient_stock'),
                Type.Literal('special_price_conflict'),
                Type.Literal('other_promos_conflict'),
              ]),
            })
          ),
          free_goods_stock_validation: Type.Optional(
            Type.Object({
              has_insufficient_stock: Type.Boolean(),
              insufficient_stock_items: Type.Array(
                Type.Object({
                  product_item_code: Type.Integer(),
                  required_quantity: Type.Integer(),
                  available_stock: Type.Integer(),
                  cart_quantity: Type.Integer(),
                  free_goods_quantity: Type.Integer(),
                })
              ),
            })
          ),
        })
      ),
      data: Type.Array(
        Type.Intersect([
          Type.Pick(ProductResponseSchema, [
            'id',
            'item_code',
            'title',
            'brand_name',
            'brand_desc',
          ]),
          Type.Pick(DbSchema['order_items'], ['price']),
          Type.Object({
            product_image: Type.String(),
            price_diff: Type.Integer(),
            stock: Type.Integer(),
            stock_diff: Type.Integer(),
            is_price_diff: Type.Boolean(),
            is_stock_diff: Type.Boolean(),
            stock_magnifier: Type.Integer(),
          }),
        ])
      ),
    }),
    forbidden_response: Type.Object({
      message: Type.String(),
      error: Type.Union([
        Type.Literal('ALL_ITEM_CANT_BE_PROCESSED'),
        Type.Literal('MIN_ORDER_NOT_REACHED'),
        Type.Literal('MIN_ORDER_NOT_REACHED_DUE_TO_TOTAL_DIFF'),
      ]),
      statusCode: Type.Number(),
      data: Type.Array(
        Type.Intersect([
          Type.Pick(ProductResponseSchema, [
            'id',
            'item_code',
            'title',
            'brand_name',
            'brand_desc',
          ]),
          Type.Pick(DbSchema['order_items'], ['price']),
          Type.Object({
            product_image: Type.String(),
            price_diff: Type.Integer(),
            stock: Type.Integer(),
            stock_diff: Type.Integer(),
            is_price_diff: Type.Boolean(),
            is_stock_diff: Type.Boolean(),
            stock_magnifier: Type.Integer(),
          }),
        ])
      ),
    }),
  },

  // ------------------------------------------------------
  // Backoffice Order ( /backoffice/ )
  // ------------------------------------------------------
  readActivities: {
    path: '',
    query: readActivitiesSchema,
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          DbSchema['orders'],
          Type.Pick(DbSchema['order_metas'], [
            'ship_to',
            'bill_to',
            'transaction_id',
            'total',
            'payment_method',
            'delivery_note',
          ]),
          Type.Object({
            promos: Nullable(PopulatedOrderPromoSchema),
            user: OrderUserSchema,
            attachments: Type.Object({
              payment_proofs: Type.Array(Type.String({ format: 'uri' })),
              po_docs: Type.Array(Type.String({ format: 'uri' })),
            }),
            client_type: Type.Union([
              Type.Literal('android'),
              Type.Literal('ios'),
              Type.Literal('web'),
              Type.Null(),
            ]),
            delivery_order_number: Type.String(),
            do_number: Type.Array(Type.String()),
            product_notes: Type.Array(Type.String()),
            cancel_reason: Nullable(Type.String()),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
      total: Type.Number(),
      total_items: Type.Number(),
    }),
  },
  exportActivities: {
    path: '/export',
    query: readActivitiesSchema,
  },
  readActivityDetail: {
    path: '/:id',
    params: paramByIDSchema,
    query: ReadActivityDetailSchema,
    response: Type.Object({
      voucher_validation: Type.Optional(
        Type.Object({
          eligible: Type.Boolean(),
          reasons: Type.Array(Type.String(), { default: [] }),
        })
      ),
      order_meta_total: Type.Number(),
      total: Type.Number(),
      payment_proofs: Type.Array(AdminOrderPaymentProofSchema),
      order: Type.Intersect([
        DbSchema['orders'],
        Type.Pick(DbSchema['order_metas'], [
          'ship_to',
          'bill_to',
          'transaction_id',
          'payment_method',
          'term_of_payment_days',
          'delivery_note',
          'delivery_option',
          'delivery_date',
          'ship_to_contact',
        ]),
        Type.Object({
          user: OrderUserSchema,
          payment: AdminOrderPaymentSchema,
          attachments: Type.Object({
            payment_proofs: Type.Array(Type.String({ format: 'uri' })),
            finalized_payment_proofs: Type.Array(AdminOrderPaymentProofSchema),
            po_docs: Type.Array(Type.String({ format: 'uri' })),
          }),
          count_items: Type.Integer({ exclusiveMinimum: 0 }),
          promos: PopulatedOrderPromoSchema,
          requires_manual_process: Type.Optional(Type.Boolean()),
          manual_process_items: Type.Optional(
            Type.Array(ManualProcessItemSchema)
          ),
        }),
      ]),
      order_items: Type.Array(
        Type.Intersect([
          Type.Omit(DbSchema['order_items'], [
            'order_id',
            'line_item',
            'line_item_sap',
          ]),
          Type.Pick(DbSchema['products'], [
            'item_code',
            'title',
            'can_overwrite_price',
            'material_division',
            'minimum_order_quantity',
            'order_quantity_multiple',
          ]),
          Type.Object({
            images: Type.Array(Type.String()),
            image_thumbnails: Type.Array(Type.String()),
            images_medium: Type.Array(Type.String()),
            images_large: Type.Array(Type.String()),
            empty_stock: Type.Object({
              status: Type.Boolean(),
              message: Nullable(Type.String()),
            }),
            voucher_rule_product: VoucherRuleProductSchema,
            has_voucher_discount: Type.Optional(Type.Boolean()),
            voucher_discount_percentage: Type.Optional(
              Type.Number({ minimum: 0, maximum: 100 })
            ),
            voucher_discount_amount: Type.Optional(Type.Number({ minimum: 0 })),
            voucher_reward_type: Type.Optional(
              Type.Union([
                Type.Literal('DISCOUNT_PERCENT'),
                Type.Literal('DISCOUNT_AMOUNT'),
              ])
            ),
            voucher_code: Type.Optional(
              Type.String({ maxLength: VOUCHER_CODE_MAX_LENGTH })
            ),
            requires_manual_process: Type.Optional(Type.Boolean()),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  readActivitiesByMetaId: {
    path: '/meta/:id',
    params: paramByIDSchema,
    response: Type.Intersect([
      Type.Pick(DbSchema['order_metas'], [
        'id',
        'ship_to',
        'bill_to',
        'transaction_id',
        'delivery_note',
        'payment_method',
        'total',
        'ship_to_contact',
        'delivery_option',
        'delivery_date',
      ]),
      Type.Object({
        /*
        declare created_at because surprisingly we dont have timestamp
        when order_meta is created, so we need to get it from orders table
        */
        created_at: Type.Integer({ exclusiveMinimum: 0 }),
        po_effective_date: Type.Integer({ exclusiveMinimum: 0 }),
        user: OrderUserSchema,
        orders: Type.Array(
          Type.Intersect([
            Type.Pick(DbSchema['orders'], [
              'id',
              'subtotal',
              'sub_po_number',
              'so_number',
              'division',
              'status',
            ]),
            Type.Object({
              promos: Nullable(PopulatedOrderPromoSchema),
              items: Type.Array(
                Type.Intersect([
                  Type.Pick(DbSchema['order_items'], [
                    'id',
                    'price',
                    'quantity',
                    'subtotal',
                    'is_new_product',
                    'product_id',
                  ]),
                  Type.Pick(DbSchema['products'], ['item_code', 'title']),
                  Type.Object({
                    images: Type.Array(Type.String()),
                    image_thumbnails: Type.Array(Type.String()),
                    images_medium: Type.Array(Type.String()),
                    images_large: Type.Array(Type.String()),
                  }),
                ])
              ),
              voucher_reward_discounts: Type.Array(
                Type.Object({
                  product_item_code: Type.Integer(),
                  product_name: Type.String(),
                  division: Type.String(),
                  quantity: Type.Integer({ minimum: 0 }),
                  original_subtotal: Type.Number({ minimum: 0 }),
                  discount_amount: Nullable(Type.Number()),
                  discount_percent: Nullable(
                    Type.Number({ minimum: 0, maximum: 100 })
                  ),
                  saved_amount: Type.Number({ minimum: 0 }),
                  final_subtotal: Type.Number({ minimum: 0 }),
                })
              ),
              voucher_reward_free_goods: Type.Array(
                Type.Object({
                  product_id: Type.String({ format: 'uuid' }),
                  product_item_code: Type.Integer(),
                  product_name: Type.String(),
                  division: Type.String(),
                  quantity: Type.Integer({ minimum: 0 }),
                  saved_amount: Type.Number({ minimum: 0 }),
                  images: Type.Array(Type.String()),
                  image_thumbnails: Type.Array(Type.String()),
                  images_medium: Type.Array(Type.String()),
                  images_large: Type.Array(Type.String()),
                })
              ),
            }),
          ])
        ),
        bank_info: Type.Union([
          Type.Pick(DbSchema['order_payments'], ['bank_name', 'bank_account']),
          Type.Null(),
        ]),

        attachments: Type.Object({
          payment_proofs: Type.Array(Type.String({ format: 'uri' })),
          finalized_payment_proofs: Type.Array(AdminOrderPaymentProofSchema),
          po_docs: Type.Array(Type.String({ format: 'uri' })),
        }),
      }),
    ]),
  },
  getDeliveryOrdersByMetaId: {
    path: '/meta/:id/delivery-orders',
    query: WarehouseIdSchema,
    params: paramByIDSchema,
    response: Type.Object({
      success: Type.Boolean(),
      data: Type.Object({
        order_meta_id: Type.String({ format: 'uuid' }),
        total_orders: Type.Integer({ minimum: 0 }),
        total_delivery_orders: Type.Integer({ minimum: 0 }),
        orders: Type.Array(
          Type.Object({
            order_id: Type.String({ format: 'uuid' }),
            order_number: Type.String(),
            sub_po_number: Type.String(),
            cancel_reason: Nullable(Type.String()),
            cancel_orders: Type.Array(
              Type.Object({
                cancel_reason: Nullable(Type.String()),
                product_name: Type.String(),
                quantity: Type.Integer(),
                sales_unit: Type.String(),
              })
            ),
            division: Type.Union([
              Type.Literal('diamond'),
              Type.Literal('trading'),
              Type.Literal('fnb'),
              Type.Literal('equipment'),
              Type.Literal('food'),
            ]),
            pending_items: Type.Array(
              Type.Intersect([
                Type.Object({
                  order_item_id: Type.String({ format: 'uuid' }),
                  item_code: Type.Integer(),
                  product_name: Type.String(),
                  sales_unit: Type.String(),
                  ordered_quantity: Type.Integer({ minimum: 0 }),
                  delivered_quantity: Type.Integer({ minimum: 0 }),
                  pending_quantity: Type.Integer({ minimum: 0 }),
                  created_at: Nullable(Type.Integer({ minimum: 0 })),
                  updated_at: Nullable(Type.Integer({ minimum: 0 })),
                }),
                Type.Pick(DbSchema['products'], ['product_storage']),
              ])
            ),
            delivery_orders: Type.Array(
              Type.Object({
                id: Type.String({ format: 'uuid' }),
                do_number: Nullable(Type.String()),
                status: Type.Union([
                  Type.Literal('processed'),
                  Type.Literal('delivered'),
                  Type.Literal('received'),
                  Type.Literal('rejected'),
                  Type.Literal('partially_received'),
                  Type.Literal('cancelled'),
                ]),
                do_processed_at: Nullable(Type.String({ format: 'date' })),
                do_delivered_at: Nullable(Type.String({ format: 'date' })),
                do_received_at: Nullable(Type.String({ format: 'date' })),
                do_rejected_at: Nullable(Type.String({ format: 'date' })),
                is_recreated: Type.Optional(Type.Boolean()),
                created_at: Type.Integer({ minimum: 0 }),
                updated_at: Type.Integer({ minimum: 0 }),
                items: Type.Array(
                  Type.Intersect([
                    Type.Object({
                      id: Type.String({ format: 'uuid' }),
                      order_item_id: Type.String({ format: 'uuid' }),
                      item_code: Type.Integer(),
                      received_quantity: Type.Integer({ minimum: 0 }),
                      delivered_quantity: Type.Integer({ minimum: 0 }),
                      ordered_quantity: Type.Integer({ minimum: 0 }),
                      pod_reason: Nullable(Type.String()),
                      product_name: Type.String(),
                      sales_unit: Type.String(),
                      created_at: Type.Integer({ minimum: 0 }),
                      updated_at: Type.Integer({ minimum: 0 }),
                    }),
                    Type.Pick(DbSchema['products'], ['product_storage']),
                  ])
                ),
              })
            ),
          })
        ),
      }),
    }),
  },
  readPaginatedOrderItems: {
    path: '/:id/item',
    params: paramByIDSchema,
    response: Type.Object({
      items: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['order_items'], [
            'id',
            'price',
            'quantity',
            'original_subtotal',
          ]),
          Type.Pick(DbSchema['products'], ['item_code', 'title']),
          Type.Object({
            images: Type.Array(Type.String()),
            image_thumbnails: Type.Array(Type.String()),
            images_medium: Type.Array(Type.String()),
            images_large: Type.Array(Type.String()),
          }),
        ])
      ),
    }),
  },
  cancelOrderBackoffice: {
    path: '/cancel/:id',
    params: paramByIDSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },
  updateActivityDetailItem: {
    path: '/:id/item/:item_id',
    params: paramOrderItemSchema,
    body: Type.Intersect([
      Type.Pick(DbSchema['order_items'], [
        'confirmed_quantity',
        'note',
        'latest_price',
        'product_storage',
        'valuation_type',
        'cancel_reason_id',
      ]),
    ]),
    response: Type.Intersect([
      DbSchema['order_items'],
      Type.Object({
        can_overwrite_price: Type.Optional(Type.Boolean()),
        item_code: Type.Optional(Type.Number()),
        original_valuation_type: Type.Optional(Type.String()),
        original_product_storage: Type.Optional(Type.String()),
        empty_stock: Type.Object({
          status: Type.Boolean(),
          message: Nullable(Type.String()),
        }),
      }),
    ]),
  },
  revalidateUpdateActivityDetailItem: {
    path: '/:id/item/:item_id/revalidate',
    params: paramOrderItemSchema,
    body: Type.Pick(DbSchema['order_items'], [
      'confirmed_quantity',
      'note',
      'latest_price',
      'product_storage',
      'valuation_type',
      'cancel_reason_id',
    ]),
    response: Type.Object({
      data: Type.Intersect([
        Type.Pick(DbSchema['order_promos'], [
          'single_discount_promos',
          'single_free_good_promos',
          'bundled_discount_promos',
          'bundled_free_good_promos',
        ]),
        Type.Object({
          is_valid: Type.Boolean(),
        }),
      ]),
    }),
  },
  updateActivityDetail: {
    path: '/:id',
    params: paramByIDSchema,
    body: Type.Pick(DbSchema['orders'], ['note', 'cancel_reason_id']),
    response: Type.Intersect([
      DbSchema['orders'],
      Type.Pick(DbSchema['order_metas'], ['delivery_note']),
    ]),
  },
  submitOrder: {
    path: '/submit/:id',
    params: paramByIDSchema,
    body: Type.Union([
      Type.Object({
        excludeVoucher: Type.Optional(Type.Boolean()),
      }),
      Type.Null(),
    ]),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  validateOrderVouchers: {
    path: '/:id/validate/vouchers',
    params: paramByIDSchema,
    response: Type.Object({
      results: Type.Array(
        Type.Object({
          code: Type.String(),
          eligible: Type.Boolean(),
          reasons: Type.Array(Type.String(), { default: [] }),
          reward: Type.Object({
            type: Type.Union([
              Type.Literal('DISCOUNT_PERCENT'),
              Type.Literal('DISCOUNT_AMOUNT'),
              Type.Literal('FREE_GOODS'),
            ]),
            discount_products: Type.Optional(
              Type.Array(
                Type.Object({
                  product_item_code: Type.Integer(),
                  product_name: Type.String(),
                  discount_percent: Nullable(
                    Type.Number({ exclusiveMinimum: 0, maximum: 100 })
                  ),
                  discount_amount: Nullable(Type.Number({ minimum: 0 })),
                })
              )
            ),
            free_goods: Type.Optional(
              Type.Array(
                Type.Object({
                  product_item_code: Type.Integer(),
                  product_name: Type.String(),
                  quantity: Type.Integer({ minimum: 0 }),
                })
              )
            ),
            rule_products: Type.Array(
              Type.Object({
                product_item_code: Type.Integer(),
                product_name: Type.String(),
                min_quantity: Nullable(Type.Integer({ minimum: 0 })),
              })
            ),
            is_bundling: Type.Boolean(),
            min_total_quantity_all_item: Nullable(Type.Integer({ minimum: 0 })),
          }),
        })
      ),
    }),
  },
  readProductRewards: {
    path: '/:id/product-rewards',
    params: paramByIDSchema,
    response: Type.Object({
      promo_rewards: Type.Array(
        Type.Object({
          source: Type.Literal('promo'),
          promo_id: Type.String({ format: 'uuid' }),
          mechanism: Type.String(),
          product_item_code: Type.String(),
          product_name: Type.String(),
          quantity: Type.Integer(),
          images: Type.Optional(
            Type.Object({
              image: Type.String(),
              thumbnail_url: Nullable(Type.String()),
              medium_image_url: Nullable(Type.String()),
              large_image_url: Nullable(Type.String()),
            })
          ),
        })
      ),
      voucher_rewards: Type.Array(
        Type.Object({
          source: Type.Literal('voucher'),
          voucher_id: Type.String({ format: 'uuid' }),
          voucher_code: Type.String(),
          voucher_name: Type.String(),
          product_item_code: Type.String(),
          product_name: Type.String(),
          quantity: Type.Integer(),
          images: Type.Optional(
            Type.Object({
              image: Type.String(),
              thumbnail_url: Nullable(Type.String()),
              medium_image_url: Nullable(Type.String()),
              large_image_url: Nullable(Type.String()),
            })
          ),
        })
      ),
    }),
  },
  readManyAdditionalProducts: {
    path: '/:order_id/additional-products',
    params: Type.Object({
      order_id: Type.String(),
    }),
    query: Type.Intersect([
      Type.Object({
        search_query: Type.String(),
      }),
      PaginationSchema,
    ]),
    response: Type.Object({
      data: Type.Array(ProductResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  checkPriceStockSAP: {
    path: '/:order_id/:product_id/additional-products/check-price-stock-sap',
    params: Type.Object({
      order_id: Type.String(),
      product_id: Type.String(),
    }),
    response: Type.Intersect([
      ProductResponseSchema,
      Type.Object({
        price_sap: Type.Integer(),
        stock_sap: Type.Integer(),
        valuation_type: Type.String(),
      }),
    ]),
  },
  submitNewProduct: {
    path: '/additional-products/submit',
    body: Type.Object({
      order_id: Type.String(),
      products: Type.Array(
        Type.Object({
          id: Type.String({ format: 'uuid' }),
          quantity: Type.Integer({ exclusiveMinimum: 0 }),
        })
      ),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  deleteAdditionalProduct: {
    path: '/:id/item',
    params: paramByIDSchema,
    body: Type.Object({
      product_ids: Type.Array(Type.String({ format: 'uuid' })),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Payment Proof ( /:order_id/payment/:id/proof )
  // ------------------------------------------------------
  uploadPaymentProof: {
    path: '/payment-proof/upload',
    body: Type.Object({
      files: TypeCustom.Custom<SavedMultipartFile[]>(),
    }),
    response: Type.Object({
      urls: Type.Array(Type.String()),
    }),
  },
  addPaymentProof: {
    path: '/meta/:order_meta_id/payment/:order_payment_id/proof',
    body: Type.Object({
      url: Type.String({ format: 'uri' }),
    }),
    params: Type.Object({
      order_meta_id: Type.String({ format: 'uuid' }),
      order_payment_id: Type.String({ format: 'uuid' }),
    }),
    response: DbSchema['order_payment_proofs'],
  },
  deletePaymentProof: {
    path: '/meta/:order_meta_id/payment/:order_payment_id/proof/:id',
    params: Type.Intersect([
      paramByIDSchema,
      Type.Object({
        order_meta_id: Type.String({ format: 'uuid' }),
        order_payment_id: Type.String({ format: 'uuid' }),
      }),
    ]),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  getFinalizedPaymentProofs: {
    path: '/payment-proof/:order_payment_id/finalized',
    params: Type.Object({
      order_payment_id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      items: Type.Array(FinalizedPaymentProofItemSchema),
    }),
  },

  // ------------------------------------------------------
  // Order Template ( /template )
  // ------------------------------------------------------
  createOrderTemplate: {
    path: '/template',
    body: Type.Intersect([
      Type.Omit(DbSchema['order_templates'], [
        'id',
        'user_id',
        'created_by',
        'note',
      ]),
      Type.Object({
        note: Type.Optional(Type.String()),
        warehouse_id: Type.String({ format: 'uuid' }),
      }),
    ]),
    response: DbSchema['order_templates'],
  },
  getManyOrderTemplate: {
    path: '/template',
    query: Type.Intersect([ReadManyOrderTemplateSchema]),
    response: Type.Object({
      data: Type.Array(DbSchema['order_templates']),
      pagination: PaginationInfoSchema,
    }),
  },
  getOneOrderTemplate: {
    path: '/template/:id',
    params: paramByIDSchema,
    response: Type.Union([DbSchema['order_templates'], Type.Null()]),
  },
  updateOrderTemplate: {
    path: '/template/:id',
    params: paramByIDSchema,
    body: Type.Intersect([
      Type.Omit(DbSchema['order_templates'], [
        'id',
        'user_id',
        'created_by',
        'note',
      ]),
      Type.Object({
        note: Type.Optional(Type.String()),
        warehouse_id: Type.String(),
      }),
    ]),
    response: DbSchema['order_templates'],
  },
  deleteOrderTemplate: {
    path: '/template/:id',
    params: paramByIDSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },
  addToCartFromOrderTemplate: {
    path: '/template/:id/add-to-cart',
    params: paramByIDSchema,
    body: WarehouseIdSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Order Template Item ( /:id/item )
  // -----------------------------------------------------
  addOrderTemplateItem: {
    path: '/template/:id/item',
    body: Type.Object({
      items: Type.Array(
        Type.Omit(DbSchema['order_template_items'], [
          'id',
          'order_template_id',
          'created_at',
          'updated_at',
        ])
      ),
    }),
    params: paramByIDSchema,
    response: Type.Object({
      items: Type.Array(DbSchema['order_template_items']),
    }),
  },
  getManyOrderTemplateItem: {
    path: '/template/:id/item',
    params: paramByIDSchema,
    query: Type.Intersect([ReadManyOrderTemplateItemSchema, WarehouseIdSchema]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['order_template_items'], ['id', 'quantity']),
          Type.Object({
            product: ProductWithPromoResponseSchema,
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  deleteProductFromOrderTemplate: {
    path: '/template/:id/item',
    params: paramByIDSchema,
    body: {
      product_id: Type.String(),
    },
    response: Type.Object({
      message: Type.String(),
    }),
  },
  deleteAllProductFromOrderTemplate: {
    path: '/template/:id/item/all',
    params: paramByIDSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },
  updateOrderTemplateItem: {
    path: '/template/:id/item',
    body: Type.Object({
      items: Type.Array(
        Type.Omit(DbSchema['order_template_items'], [
          'id',
          'order_template_id',
          'created_at',
          'updated_at',
        ])
      ),
    }),
    params: paramByIDSchema,
    response: Type.Object({
      items: Type.Array(DbSchema['order_template_items']),
    }),
  },

  // ------------------------------------------------------
  // Minimum Spend ( /minimum-spend )
  // ------------------------------------------------------
  readManyMinimumSpend: {
    path: '/minimum-spend',
    response: Type.Object({
      key: Nullable(Type.String()),
      url: Nullable(Type.String()),
      data: Type.Array(Type.Omit(DbSchema['minimum_spends'], ['url', 'key'])),
    }),
  },
  uploadMinimumSpend: {
    path: '/minimum-spend/upload',
    body: Type.Object({
      file: TypeCustom.Custom<SavedMultipartFile>(),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  deleteMinimumSpend: {
    path: '/minimum-spend',
    body: Type.Object({
      key: Type.String(),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  downloadTemplateMinimumSpend: {
    path: '/minimum-spend/download/template',
  },
  readOneMinimumSpend: {
    path: '/minimum-spend',
    query: Type.Object({
      warehouse: Type.String({
        format: 'uuid',
      }),
    }),
    response: DbSchema['minimum_spends'],
  },

  // ------------------------------------------------------
  // Order Status History ( /status-history )
  // ------------------------------------------------------
  getManyOrderStatusHistory: {
    path: '/:order_id/status-history',
    query: OrderBySchema,
    params: Type.Object({
      order_id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Array(
      Type.Object({
        id: Type.String({ format: 'uuid' }),
        order_id: Type.String({ format: 'uuid' }),
        status: Type.String(),
        note: Nullable(Type.String({ nullable: true })),
        origin: Type.String(),
        client_type: Type.String(),
        client_os: Type.String(),
        client_device: Type.String(),
        client_version: Type.String(),
        created_by: Nullable(OrderUserSchema),
        created_at: Type.Number({ minimum: 0 }),
      })
    ),
  },

  // ------------------------------------------------------
  // Order Changelog
  // ------------------------------------------------------
  getOrderChangelogs: {
    path: '/:id/changelog',
    params: paramByIDSchema,
    response: Type.Array(
      Type.Intersect([
        Type.Omit(OrderChangelog, ['order_id', 'created_by']),
        Type.Object({
          created_by: Type.Union([Type.Null(), Type.String()]),
        }),
      ])
    ),
  },

  // ------------------------------------------------------
  // Cancel Reason
  // ------------------------------------------------------
  getManyCancelReason: {
    path: '/cancel-reason',
    query: PaginationSchema,
    response: Type.Object({
      data: Type.Array(Type.Omit(CancelReason, ['id'])),
      pagination: PaginationInfoSchema,
    }),
  },

  // ------------------------------------------------------
  // Smart Search Dropdown List
  // ------------------------------------------------------
  getCustomerType: {
    path: '/customer-type',
    response: Type.Object({
      data: Type.Array(
        Type.Object({
          cg_1_business_segmentation: Type.String(),
          cg_1_business_segmentation_desc: Type.String(),
        })
      ),
    }),
  },
};

export type OrderCrud = RecursiveStatic<typeof OrderCrudSchema>;
